import { memo, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { DotLegalAlert, DotLegalFullScreenSpinner } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useDocumentPreviewStyles } from "../DocumentPreview.styles";
import DocumentPreviewToolbar from "../DocumentPreviewToolbar";
import { IDocumentNavigation, IDocumentPreviewToolbarAction } from "../DocumentPreview.types";
import { useDocumentKeyboardNavigation } from "../DocumentPreview.hooks";
import { useCtrlWheelZoom } from "../useCtrlWheelZoom";
import { deriveCitationAnchorSets, findAnchorRangeInTextNodes, unwrapMarks, wrapRangeInMarks } from "../documentSearch";
import TextSelectionLayer from "../textSelection/TextSelectionLayer";
import { TEXT_VIEW_TEXT_SELECTION_DOM_CONFIG } from "../textSelection/textSelection.domConfigs";
import { ITextSelectionConfig } from "../textSelection/textSelection.types";

const CITATION_MARK_ATTRIBUTE = "data-text-view-citation";
const BASE_FONT_SIZE = 14;
const PAGE_MAX_WIDTH = 900;

export interface ITextViewProps {
    pages: Array<string>;
    isLoading: boolean;
    zoomLevel: number;
    zoomInputValue: string;
    onZoomBy?: (factor: number) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onZoomInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onZoomInputBlur: () => void;
    onDownload: () => void;
    isDownloading: boolean;
    documentNavigation?: IDocumentNavigation;
    toolbarAction?: IDocumentPreviewToolbarAction;
    citationChunkContent?: string;
    onCitationResolved?: (found: boolean) => void;
    textSelection?: ITextSelectionConfig;
}

// Memoised so the marks the citation pass writes into this page's DOM survive re-renders of the viewer
// (a zoom click, a page change) that would otherwise have React rebuild the markdown from scratch.
const TextViewPage = memo(function TextViewPage(props: { pageNumber: number; content: string }) {
    return (
        <Box
            component="section"
            data-text-page={props.pageNumber}
            sx={{
                width: "100%",
                maxWidth: PAGE_MAX_WIDTH,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: 4,
                marginBottom: 3,
                "& p": { marginTop: 0, marginBottom: 1.5 },
                "& h1, & h2, & h3, & h4, & h5, & h6": { marginTop: 2, marginBottom: 1 },
                "& table": { borderCollapse: "collapse", width: "100%" },
                "& td, & th": { border: "1px solid", borderColor: "divider", padding: 0.5 },
                "& mark": { backgroundColor: "rgba(255, 235, 59, 0.4)" },
            }}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{props.content}</ReactMarkdown>
        </Box>
    );
});

export default function TextView(props: ITextViewProps) {
    const styles = useDocumentPreviewStyles();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    useCtrlWheelZoom(scrollContainerRef, props.onZoomBy);
    const { translateString } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const pageRefs = useRef<Array<HTMLElement>>([]);
    const isManualNavigationRef = useRef(false);
    const manualNavigationTimeoutRef = useRef<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInputValue, setPageInputValue] = useState("1");

    useDocumentKeyboardNavigation(props.documentNavigation);

    const collectPages = () => {
        const container = containerRef.current;
        pageRefs.current = container ? Array.from(container.querySelectorAll<HTMLElement>("section[data-text-page]")) : [];
        return pageRefs.current;
    };

    const goToPage = (pageNumber: number) => {
        const pages = collectPages();
        if (pages.length === 0) {
            return;
        }
        const validPage = Math.max(1, Math.min(pageNumber, pages.length));
        setCurrentPage(validPage);
        setPageInputValue(String(validPage));

        isManualNavigationRef.current = true;
        if (manualNavigationTimeoutRef.current) {
            window.clearTimeout(manualNavigationTimeoutRef.current);
        }
        manualNavigationTimeoutRef.current = window.setTimeout(() => {
            isManualNavigationRef.current = false;
        }, 500);

        pages[validPage - 1].scrollIntoView({ block: "start", behavior: "auto" });
    };

    useEffect(() => {
        const pages = collectPages();
        if (pages.length === 0) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (isManualNavigationRef.current) return;
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => pages.indexOf(a.target as HTMLElement) - pages.indexOf(b.target as HTMLElement));
                if (visible.length === 0) return;
                const topPageIndex = pages.indexOf(visible[0].target as HTMLElement);
                if (topPageIndex < 0) return;
                setCurrentPage(topPageIndex + 1);
                setPageInputValue(String(topPageIndex + 1));
            },
            { threshold: [0, 0.25, 0.5] }
        );
        pages.forEach((page) => observer.observe(page));

        return () => {
            observer.disconnect();
            if (manualNavigationTimeoutRef.current) {
                window.clearTimeout(manualNavigationTimeoutRef.current);
                manualNavigationTimeoutRef.current = null;
            }
        };
    }, [props.pages]);

    const { onCitationResolved } = props;
    useEffect(() => {
        const container = containerRef.current;
        if (!container || props.pages.length === 0) {
            return;
        }

        unwrapMarks(container, CITATION_MARK_ATTRIBUTE);

        const anchorSets = deriveCitationAnchorSets(props.citationChunkContent);
        if (anchorSets.length === 0) {
            return;
        }

        let found = false;
        for (const anchors of anchorSets) {
            const position = findAnchorRangeInTextNodes(container, anchors);
            if (position !== null && wrapRangeInMarks(container, position, CITATION_MARK_ATTRIBUTE)) {
                found = true;
            }
        }
        if (found) {
            container.querySelector(`mark[${CITATION_MARK_ATTRIBUTE}]`)?.scrollIntoView({ block: "center", behavior: "auto" });
        }
        onCitationResolved?.(found);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.citationChunkContent, props.pages]);

    const handlePageInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            const pageNumber = parseInt(pageInputValue, 10);
            if (!isNaN(pageNumber)) goToPage(pageNumber);
        }
    };

    const handlePageInputBlur = () => {
        const pageNumber = parseInt(pageInputValue, 10);
        if (!isNaN(pageNumber)) goToPage(pageNumber);
        else setPageInputValue(String(currentPage));
    };

    return (
        <Box sx={styles.imageViewerContainer}>
            <DocumentPreviewToolbar
                zoomLevel={props.zoomLevel}
                zoomInputValue={props.zoomInputValue}
                onZoomIn={props.onZoomIn}
                onZoomOut={props.onZoomOut}
                onZoomInputChange={props.onZoomInputChange}
                onZoomInputKeyDown={props.onZoomInputKeyDown}
                onZoomInputBlur={props.onZoomInputBlur}
                onDownload={props.onDownload}
                isDownloading={props.isDownloading}
                documentNavigation={props.documentNavigation}
                toolbarAction={props.toolbarAction}
                pageSelector={
                    props.pages.length > 0
                        ? {
                              currentPage,
                              numPages: props.pages.length,
                              pageInputValue,
                              onPageInputChange: (event) => setPageInputValue(event.target.value),
                              onPageInputKeyDown: handlePageInputKeyDown,
                              onPageInputBlur: handlePageInputBlur,
                              onPreviousPage: () => goToPage(currentPage - 1),
                              onNextPage: () => goToPage(currentPage + 1),
                          }
                        : undefined
                }
            />
            <DotLegalAlert severity="info" warningText={translateString("textViewPdfExtractionBanner")} marginTop={0} marginBottom={0} />
            <Box sx={styles.textViewContainer} ref={scrollContainerRef}>
                {props.isLoading && (
                    <Box sx={styles.loadingOverlay}>
                        <DotLegalFullScreenSpinner />
                    </Box>
                )}
                <Box
                    ref={containerRef}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                        paddingTop: 3,
                        fontSize: BASE_FONT_SIZE * props.zoomLevel,
                    }}
                >
                    {props.pages.map((content, index) => (
                        <TextViewPage key={index} pageNumber={index + 1} content={content} />
                    ))}
                </Box>
                {props.textSelection && (
                    <TextSelectionLayer
                        config={props.textSelection}
                        rootRef={containerRef}
                        domConfig={TEXT_VIEW_TEXT_SELECTION_DOM_CONFIG}
                        resetKey={`text-view-${props.pages.length}`}
                    />
                )}
            </Box>
        </Box>
    );
}
